import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Loader2, X, Check, ArrowLeft, Search, Tag, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { Label } from '../UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../UI/dialog';
import { Drawer, DrawerContent } from '../UI/drawer';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from '../UI/alert-dialog';
import useIsMobile from '../../Hooks/useIsMobile';
import apiService from '../../Services/APIService';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';
import { cn } from '../../Lib/Cn';
import { getUserConfig } from '../../Store/Reducers';

const MAX_CATEGORIES = 10;

const CTG_COLORS = [
    '#000000', '#434343', '#0d1f2d', '#4a148c', '#0c6980', '#004d40', '#043d2a',
    '#0b5d1e', '#d4af37', '#cd7f32', '#bf5700', '#8f5d46', '#841617', '#6a0a0a'
];

const manageCategoryAPI = async (key, payload) => {
    const result = await apiService.sendChange(4, 'profile', {
        updateType: 'TSKCTG',
        key,
        changeType: 'TSKCTG',
        targets: JSON.stringify(payload),
    });
    checkAPIResponse(result);
    return result;
};

// ── Mobile: Drawer with two-view navigation ────────────────────────────────────
const MobileAddForm = ({ categories, onSave, onCancel, saving, initial = null }) => {
    const [name, setName] = useState(initial?.CNm || '');
    const nameInputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => nameInputRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, []);

    // Dynamically default to the first unused color in the palette
    const activeColors = categories.filter(c => !c.IsD).map(c => c.Clr);
    const firstUnusedColor = CTG_COLORS.find(hex => !activeColors.includes(hex)) || CTG_COLORS[0];
    const [color, setColor] = useState(initial?.Clr || firstUnusedColor);

    const IsDuplicate = name.trim().length > 0 && categories.some(
        c => !c.IsD && c.CNm.trim().toLowerCase() === name.trim().toLowerCase()
            && (!initial || c.CID !== initial.CID)
    );
    const canSave = name.trim().length > 0 && !IsDuplicate;
    const handleSave = () => { if (canSave) onSave({ ...(initial ? { CID: initial.CID } : {}), CNm: name.trim(), Clr: color }); };

    return (
        <div className="flex flex-col gap-4 px-4 pb-4 pt-3">
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-700">Name</Label>
                    <span className="text-[11px] text-sub-text-color">{name.length}/50</span>
                </div>
                <Input
                    ref={nameInputRef}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && canSave && handleSave()}
                    placeholder="e.g. Vendor Follow-ups"
                    maxLength={50}
                    className="h-10 text-sm"
                />
                {IsDuplicate && <p className="text-xs text-red-500">A growth area with this name already exists.</p>}
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-gray-700">Display Color</Label>
                <div className="flex flex-wrap gap-2">
                    {CTG_COLORS.map((hex, i) => {
                        const isUsed = categories.some(c => !c.IsD && c.Clr === hex && (!initial || c.CID !== initial.CID));
                        return (
                            <button key={i} type="button" onClick={() => setColor(hex)}
                                className={cn(
                                    'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none',
                                    color === hex
                                        ? 'ring-2 ring-offset-2 ring-sahi-primary-color'
                                        : 'border border-gray-300 hover:border-sahi-primary-color/50'
                                )}
                                style={{ backgroundColor: hex }} title={isUsed ? `${hex} (Already in use)` : hex}>
                                {color === hex ? (
                                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                                ) : isUsed ? (
                                    <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-gray-700">Preview</Label>
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-lg border"
                        style={{ backgroundColor: color + '22', borderColor: color + '66', color }}>
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        {name.trim() || 'Growth area name'}
                    </span>
                </div>
            </div>
            <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={onCancel} className="sahi-secondary-button flex-1" disabled={saving}>
                    <X className="h-3.5 w-3.5" /> Close
                </Button>
                <Button onClick={handleSave} disabled={!canSave || saving} className="sahi-primary-button flex-1">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    Save Growth Area
                </Button>
            </div>
        </div>
    );
};

const MobileDrawerBody = ({ categories, onAdd, onEdit, onDelete, initialView, onClose, selectedCID, onSelect, open }) => {
    const [view, setView] = useState(initialView);

    useEffect(() => {
        if (open) setView(initialView);
    }, [open, initialView]);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

    const active = categories.filter(c => !c.IsD);
    const showSearch = false;
    const filtered = [...active]
        .sort((a, b) => a.CNm.localeCompare(b.CNm))
        .filter(c => c.CNm.toLowerCase().includes(search.toLowerCase()));

    const handleAdd = async (payload) => {
        setSaving(true);
        try {
            const savedCID = await onAdd(payload);
            if (!savedCID) { setView('list'); setSearch(''); }
        }
        catch (err) { toast.error(err.message || 'Could not add growth area.'); }
        finally { setSaving(false); }
    };

    const handleEditSave = async (payload) => {
        setSaving(true);
        try {
            const savedCID = await onEdit(payload);
            if (!savedCID) { setView('list'); setEditTarget(null); }
        }
        catch (err) { toast.error(err.message || 'Could not update growth area.'); }
        finally { setSaving(false); }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try { await onDelete({ CID: deleteTarget.CID }); setDeleteAlertOpen(false); setDeleteTarget(null); }
        catch (err) { toast.error(err.message || 'Could not delete growth area.'); }
        finally { setSaving(false); }
    };

    if (view === 'add') {
        return (
            <>
                <div className="flex items-center justify-between px-4 h-16 shrink-0  ">
                    <div className="flex gap-4 items-center">
                        <button type="button" tabIndex={-1} onClick={() => setView('list')}
                            className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500">
                            <ArrowLeft className="h-[24px] w-[24px]" />
                        </button>
                        <p className="text-lg font-semibold text-gray-900">New Growth Area</p>
                    </div>
                </div>
                <div className="overflow-y-auto custom-scroll flex-1">
                    <MobileAddForm categories={categories} onSave={handleAdd} onCancel={() => setView('list')} saving={saving} />
                </div>
            </>
        );
    }

    if (view === 'edit') {
        return (
            <>
                <div className="flex items-center justify-between px-4 h-16 shrink-0  ">
                    <div className="flex gap-4 items-center">
                        <button type="button" tabIndex={-1} onClick={() => { setView('list'); setEditTarget(null); }}
                            className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500">
                            <ArrowLeft className="h-[24px] w-[24px]" />
                        </button>
                        <p className="text-lg font-semibold text-gray-900">Edit Growth Area</p>
                    </div>
                </div>
                <div className="overflow-y-auto custom-scroll flex-1">
                    <MobileAddForm categories={categories} onSave={handleEditSave}
                        onCancel={() => { setView('list'); setEditTarget(null); }}
                        saving={saving} initial={editTarget} />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="flex items-center justify-between px-4 h-16 shrink-0  ">
                <div className="flex gap-4 items-center">
                    <button type="button" tabIndex={-1} onClick={onClose}
                        className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500">
                        <ArrowLeft className="h-[24px] w-[24px]" />
                    </button>
                    <p className="text-lg font-semibold text-gray-900">Select Growth Area</p>
                </div>
                <button type="button" onClick={() => {
                    if (active.length >= MAX_CATEGORIES) { toast.error(`You can only add up to ${MAX_CATEGORIES} growth areas.`); return; }
                    setView('add');
                }}
                    className="w-auto h-auto p-0 hover:bg-zinc-100 flex items-center justify-center text-gray-500">
                    <Plus className="h-[24px] w-[24px]" />
                </button>
            </div>
            {active.length > 3 && showSearch && (
                <div className="px-4 pb-3 shrink-0">
                    <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                        <Input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search"
                            className="pl-10 h-10 w-full" />
                    </div>
                </div>
            )}
            <div className={`overflow-y-auto custom-scroll flex-1 px-4 ${!showSearch ? 'pt-0' : ''} py-1`}>
                {filtered.length > 0 ? (
                    <div className="flex flex-col">
                        {filtered.map(cat => {
                            const isSel = onSelect && cat.CID === selectedCID;
                            return (
                                <div key={cat.CID}
                                    className={cn(
                                        'flex items-center gap-2.5 py-2.5 min-h-[48px] pl-3 sahi-radius px-2 transition-colors cursor-pointer active:bg-gray-50',
                                        isSel && 'bg-white'
                                    )}
                                    onClick={() => {
                                        if (onSelect) { onSelect(isSel ? 0 : cat.CID); onClose(); }
                                        else { setEditTarget(cat); setView('edit'); }
                                    }}>
                                    <div className="w-4 h-4 rounded-full shrink-0 flex items-center justify-center transition-all duration-200" style={{ backgroundColor: cat.Clr }}>
                                        {isSel && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                                    </div>
                                    <span className="flex-1 text-sm font-medium text-text-color truncate">{cat.CNm}</span>
                                    <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 p-0"
                                            onClick={() => { setEditTarget(cat); setView('edit'); }}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                                            onClick={() => { setDeleteTarget(cat); setDeleteAlertOpen(true); }}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Tag className="h-8 w-8 text-gray-200 mb-2" />
                        <p className="text-sm text-sub-text-color">
                            {search ? `No growth areas match "${search}"` : 'No growth areas yet. Add one below.'}
                        </p>
                    </div>
                )}
            </div>
            <div className="px-4 pb-4 pt-2 border-t border-zinc-100 shrink-0">
                <Button className="sahi-primary-button w-full" onClick={() => {
                    if (active.length >= MAX_CATEGORIES) { toast.error(`You can only add up to ${MAX_CATEGORIES} growth areas.`); return; }
                    setView('add');
                }}>
                    <Plus className="h-4 w-4" /> Add New Growth Area
                </Button>
            </div>
            <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to delete this growth area?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {`Delete "${deleteTarget?.CNm}"? This won't affect tasks that already have this growth area.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="!justify-start gap-3">
                        <Button variant="outline" onClick={() => { setDeleteAlertOpen(false); setDeleteTarget(null); }} className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]">
                            <X className="w-3 h-3" /> Close
                        </Button>
                        <Button onClick={handleDeleteConfirm} disabled={saving} className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[120px]">
                            <Trash2 className="w-3 h-3" /> Delete Growth Area
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ── Desktop: Masters-identical Dialog ─────────────────────────────────────────
const DesktopDialog = ({ open, onOpenChange, categories, onAdd, onEdit, onDelete, initialView, selectedCID, onSelect }) => {
    const [mode, setMode] = useState(initialView === 'add' ? 'add' : 'list'); // 'list' | 'add' | 'edit'
    const [searchTerm, setSearchTerm] = useState('');
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');

    const getFirstUnusedColor = (cats) => {
        const activeColors = cats.filter(c => !c.IsD).map(c => c.Clr);
        return CTG_COLORS.find(hex => !activeColors.includes(hex)) || CTG_COLORS[0];
    };

    const [color, setColor] = useState(() => getFirstUnusedColor(categories));
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
    const nameInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMode(initialView === 'add' ? 'add' : 'list');
            setSearchTerm('');
            setName('');
            setEditTarget(null);
        }
    }, [open, initialView]);

    useEffect(() => {
        if (mode === 'add' || mode === 'edit') {
            const t = setTimeout(() => nameInputRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [mode]);

    const active = categories.filter(c => !c.IsD);
    const filtered = [...active]
        .sort((a, b) => a.CNm.localeCompare(b.CNm))
        .filter(c => c.CNm.toLowerCase().includes(searchTerm.toLowerCase()));

    const IsDuplicate = name.trim().length > 0 && active.some(
        c => c.CNm.trim().toLowerCase() === name.trim().toLowerCase()
            && (!editTarget || c.CID !== editTarget.CID)
    );
    const canSave = name.trim().length > 0 && !IsDuplicate;

    const resetForm = () => {
        setName('');
        setColor(getFirstUnusedColor(categories));
    };

    const handleClose = () => { onOpenChange(false); };

    const handleCloseEntry = () => {
        setMode('list');
        setEditTarget(null);
        resetForm();
    };

    const openEdit = (cat) => {
        setEditTarget(cat);
        setName(cat.CNm);
        setColor(cat.Clr);
        setMode('edit');
    };

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            if (mode === 'edit') {
                const savedCID = await onEdit({ CID: editTarget.CID, CNm: name.trim(), Clr: color });
                if (savedCID) return; // parent handled close+select
            } else {
                const savedCID = await onAdd({ CNm: name.trim(), Clr: color });
                setSearchTerm('');
                if (savedCID) return; // parent handled close+select
            }
            handleCloseEntry();
        } catch (err) {
            toast.error(err.message || (mode === 'edit' ? 'Could not update growth area.' : 'Could not add growth area.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await onDelete({ CID: deleteTarget.CID });
            setDeleteAlertOpen(false);
            setDeleteTarget(null);
        } catch (err) {
            toast.error(err.message || 'Could not delete growth area.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md w-full" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                    {/* Mobile header row */}
                    <div className="sm:hidden flex items-center justify-between w-full h-16 px-1 -mx-1">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                            <button type="button" className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500"
                                onClick={mode !== 'list' ? handleCloseEntry : handleClose}>
                                <ArrowLeft className="h-[24px] w-[24px]" />
                            </button>
                            <DialogTitle className="text-lg font-semibold text-gray-900 truncate">
                                {mode === 'edit' ? 'Edit Growth Area' : mode === 'add' ? 'New Growth Area' : 'Select Growth Area'}
                            </DialogTitle>
                        </div>
                        {mode === 'list' && (
                            <Button variant="ghost" size="icon" onClick={() => {
                                if (active.length >= MAX_CATEGORIES) { toast.error(`You can only add up to ${MAX_CATEGORIES} growth areas.`); return; }
                                setMode('add'); resetForm();
                            }}
                                className=" w-auto h-auto p-0 hover:bg-zinc-100 flex items-center justify-center text-gray-500 rounded-full">
                                <Plus className=" h-[24px] w-[24px]" />
                            </Button>
                        )}
                    </div>

                    {/* Desktop title */}
                    <DialogTitle className="hidden sm:block text-lg md:text-xl">
                        {mode === 'edit' ? 'Edit Growth Area' : mode === 'add' ? 'New Growth Area' : 'Select Growth Area'}
                    </DialogTitle>

                    {/* Desktop close button */}
                    <div className="hidden sm:flex items-center gap-2">
                        <Button variant="ghost" size="icon"
                            className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
                            tabIndex={-1}
                            onClick={mode !== 'list' ? handleCloseEntry : handleClose}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                </DialogHeader>

                {/* Search bar — list mode only, same condition as Masters */}
                {mode === 'list' && active.length >= 5 && (
                    <div className="px-4">
                        <div className="relative w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                            <Input placeholder="Search Growth Area" value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="pl-10 h-10 w-full" />
                        </div>
                    </div>
                )}

                {/* List view */}
                {mode === 'list' && (
                    <div className="p-0">
                        <div className="overflow-auto px-4 lg:max-h-[60vh] flex flex-col custom-scroll">
                            {filtered.length > 0 ? (
                                <div className="space-y-3">
                                    {filtered.map(cat => {
                                        const isSel = onSelect && cat.CID === selectedCID;
                                        return (
                                            <div key={cat.CID}
                                                className={`flex items-center justify-between bg-white shadow-sm border sahi-radius pl-3 px-2 py-2 w-full transition-all ${onSelect ? 'cursor-pointer' : 'cursor-pointer'} ${isSel ? 'border-sahi-primary-color bg-gray-50/50' : 'border-gray-200'}`}
                                                onClick={() => { if (onSelect) { onSelect(isSel ? 0 : cat.CID); handleClose(); } else { openEdit(cat); } }}>
                                                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                                    <div className="w-4 h-4 rounded-full shrink-0 flex items-center justify-center transition-all duration-200" style={{ backgroundColor: cat.Clr }}>
                                                        {isSel && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                                                    </div>
                                                    <span className="flex-1 text-sm text-text-color break-all py-2">{cat.CNm}</span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                                    <Button variant="ghost" size="icon"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => openEdit(cat)}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon"
                                                        className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                                                        onClick={() => { setDeleteTarget(cat); setDeleteAlertOpen(true); }}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="w-fit text-sm text-sub-text-color lg:whitespace-nowrap bg-sahi-white sahi-radius px-5 py-2 border">
                                    No growth areas found.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Entry / Edit form */}
                {mode !== 'list' && (
                    <div className="p-0">
                        <div className="px-4">
                            <div className="pt-1 space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="ctgNameInput" className="text-sm font-medium text-gray-700">Growth Area</Label>
                                        <span className="text-[11px] text-sub-text-color">{name.length}/50</span>
                                    </div>
                                    <Input id="ctgNameInput" ref={nameInputRef} placeholder="e.g. Vendor Follow-ups"
                                        value={name} onChange={e => setName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                                        className="h-10 text-sm"
                                        maxLength={50} autoComplete="off" />
                                    {IsDuplicate && <p className="text-xs text-red-500">A growth area with this name already exists.</p>}
                                </div>

                                <div className="space-y-2 !mt-4">
                                    <Label className="text-sm font-medium text-gray-700">Display Color</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {CTG_COLORS.map((hex, i) => {
                                            const isUsed = active.some(c => c.Clr === hex && (!editTarget || c.CID !== editTarget.CID));
                                            return (
                                                <button key={i} type="button"
                                                    className={cn(
                                                        'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none',
                                                        color === hex
                                                            ? 'ring-2 ring-offset-2 ring-sahi-primary-color'
                                                            : 'border border-gray-300 hover:border-sahi-primary-color/50'
                                                    )}
                                                    style={{ backgroundColor: hex }}
                                                    onClick={() => setColor(hex)}
                                                    title={isUsed ? `${hex} (Already in use)` : hex}>
                                                    {color === hex ? (
                                                        <Check className="h-4 w-4 text-white" strokeWidth={3} />
                                                    ) : isUsed ? (
                                                        <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
                                                    ) : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="space-y-2 !mt-4">
                                    <Label className="text-sm font-medium text-gray-700">Preview</Label>
                                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-lg border"
                                            style={{ backgroundColor: color + '22', borderColor: color + '66', color }}>
                                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                                            {name.trim() || 'Growth area name'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bottom buttons — matching Masters exactly */}
                {mode !== 'list' ? (
                    <div className="flex sm:flex-row gap-3 px-4 pb-4 pt-2">
                        <Button onClick={handleSave} disabled={!canSave || saving} className="sahi-primary-button sm:flex-none sm:min-w-[140px]">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={3} />}
                            {mode === 'edit' ? 'Update Growth Area' : 'Save Growth Area'}
                        </Button>
                        <Button variant="outline" onClick={handleCloseEntry} disabled={saving} className="sahi-secondary-button sm:flex-none sm:min-w-[100px]">
                            <X className="w-3 h-3" /> Close
                        </Button>
                    </div>
                ) : (
                    <div className="flex sm:flex-row gap-3 px-4 pb-4">
                        <Button onClick={() => {
                            if (active.length >= MAX_CATEGORIES) { toast.error(`You can only add up to ${MAX_CATEGORIES} growth areas.`); return; }
                            setMode('add'); resetForm();
                        }} className="sahi-primary-button sm:flex-none sm:min-w-[140px]">
                            <Plus className="h-4 w-4" /> Add Growth Area
                        </Button>
                        <Button variant="outline" onClick={handleClose} className="sahi-secondary-button sm:flex-none sm:min-w-[100px]">
                            <X className="w-3 h-3" /> Close
                        </Button>
                    </div>
                )}

                <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure you want to delete this growth area?</AlertDialogTitle>
                            <AlertDialogDescription>
                                {`Delete "${deleteTarget?.CNm}"? This won't affect tasks that already have this growth area.`}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="!justify-start gap-3">
                            <Button onClick={handleDeleteConfirm} disabled={saving} className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[120px]">
                                <Trash2 className="w-3 h-3" /> Delete Growth Area
                            </Button>
                            <Button variant="outline" onClick={() => { setDeleteAlertOpen(false); setDeleteTarget(null); }} className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]">
                                <X className="w-3 h-3" /> Close
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
};

const useVisualViewportHeight = () => {
    const [height, setHeight] = useState(() => window.visualViewport ? window.visualViewport.height : window.innerHeight);

    useEffect(() => {
        if (!window.visualViewport) return;
        const handleResize = () => {
            setHeight(window.visualViewport.height);
        };
        window.visualViewport.addEventListener('resize', handleResize);
        return () => window.visualViewport.removeEventListener('resize', handleResize);
    }, []);

    return height;
};

// ── Main export ────────────────────────────────────────────────────────────────
const TaskCategoryManager = ({ open, onOpenChange, categories = [], onCategoriesChange, initialView = 'list', selectedCID, onSelect }) => {
    const isMobile = useIsMobile();
    const vvHeight = useVisualViewportHeight();

    const handleAdd = async (payload) => {
        if (categories.filter(c => !c.IsD).length >= MAX_CATEGORIES) {
            toast.error(`You can only add up to ${MAX_CATEGORIES} growth areas.`);
            return;
        }
        await manageCategoryAPI('ADDCTG', payload);

        let newCID = Math.max(0, ...categories.map(c => c.CID)) + 1; // fallback only
        try {
            const freshCategories = JSON.parse(getUserConfig()?.TaskCtgs || '[]');
            const matched = freshCategories.find(c => !c.IsD && c.CNm === payload.CNm);
            if (matched) {
                newCID = matched.CID;
            }
        } catch (err) {
            console.error('Failed to resolve real CID for newly added category:', err);
        }
        onCategoriesChange([...categories, { CID: newCID, IsDef: false, IsD: false, ...payload }]);
        if (onSelect) { onSelect(newCID); onOpenChange(false); return newCID; }
    };

    const handleEdit = async (payload) => {
        await manageCategoryAPI('UPDCTG', payload);
        onCategoriesChange(categories.map(c => c.CID === payload.CID ? { ...c, CNm: payload.CNm, Clr: payload.Clr } : c));
        if (onSelect) { onSelect(payload.CID); onOpenChange(false); return payload.CID; }
    };

    const handleDelete = async (payload) => {
        await manageCategoryAPI('DLTCTG', payload);
        onCategoriesChange(categories.map(c => c.CID === payload.CID ? { ...c, IsD: true } : c));
    };

    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={onOpenChange}>
                <DrawerContent
                    className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
                    style={{
                        maxHeight: `${vvHeight * 0.85}px`,
                        height: 'auto'
                    }}
                >
                    <div className="flex flex-col" style={{ maxHeight: `${vvHeight * 0.85}px` }}>
                        <MobileDrawerBody
                            categories={categories}
                            onAdd={handleAdd}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            initialView={initialView}
                            onClose={() => onOpenChange(false)}
                            selectedCID={selectedCID}
                            onSelect={onSelect}
                            open={open}
                        />
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <DesktopDialog
            open={open}
            onOpenChange={onOpenChange}
            categories={categories}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
            initialView={initialView}
            selectedCID={selectedCID}
            onSelect={onSelect}
        />
    );
};

export default TaskCategoryManager;
